import { createContext, ReactNode, RefObject, use, useRef, useState } from 'react'

import { AstraPopover, NativePopoverProps } from './AstraPopover'

type ConstrainedPopoverProps = Omit<NativePopoverProps, 'children' | 'popoverContent' | 'isOpen' | 'onOpenChange' | 'anchorRef' | 'isContextPopover'>

type AstraPopoverContextType = {
    isOpen: boolean
    anchorRef: RefObject<HTMLElement | null>
    openPopover: (element: HTMLElement, popoverContent: ReactNode, onOpenChange?: (open: boolean) => void) => void
    closePopover: () => void
}

const AstraPopoverContext = createContext<AstraPopoverContextType | undefined>(undefined)

const DEFAULT_CONFIG: ConstrainedPopoverProps = {
    isModal: true,
    noPadding: true,
    align: 'start',
    showOverlay: true,
    side: 'right',
}
type PopoverProviderProps = {
    children: ReactNode
    popoverConfig?: ConstrainedPopoverProps
    noUnstableEffects?: boolean
}

export const PopoverProvider = ({ children, popoverConfig = DEFAULT_CONFIG, noUnstableEffects = false }: PopoverProviderProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [popoverContent, setPopoverContent] = useState<ReactNode>()
    const providerAnchorRef = useRef<HTMLDivElement | null>(null)
    const onOpenChangeRef = useRef<(open: boolean) => void>(null)

    const setOpen = (open: boolean) => {
        setIsOpen(open)
        onOpenChangeRef.current?.(open)

        if (!open) {
            onOpenChangeRef.current = null
        }
    }

    const openPopover = (element: HTMLElement, popoverContent: ReactNode, onOpenChange?: (open: boolean) => void) => {
        onOpenChangeRef.current = onOpenChange ?? null
        setPopoverContent(popoverContent)
        const rect = element.getBoundingClientRect()
        if (providerAnchorRef.current) {
            providerAnchorRef.current.style.position = 'fixed'
            providerAnchorRef.current.style.top = `${rect.top}px`
            providerAnchorRef.current.style.left = `${rect.left + rect.width}px`
        }

        setOpen(true)
    }

    const closePopover = () => {
        setOpen(false)
    }

    return (
        <AstraPopoverContext
            value={{
                isOpen,
                anchorRef: providerAnchorRef,
                openPopover,
                closePopover,
            }}
        >
            {children}
            <div ref={providerAnchorRef} aria-hidden="true" style={{ position: 'fixed', width: 0, height: 0, pointerEvents: 'none', overflow: 'hidden' }} />
            <AstraPopover
                {...popoverConfig}
                isOpen={isOpen}
                onOpenChange={setOpen}
                anchorRef={providerAnchorRef}
                popoverContent={popoverContent}
                reducedMotion={noUnstableEffects}
            />
        </AstraPopoverContext>
    )
}

export const useAstraPopover = () => {
    const context = use(AstraPopoverContext)
    if (!context) {
        throw new Error('useAstraPopover must be used within a PopoverProvider')
    }
    return context
}
